import { BedDouble, MapPin, Palmtree, ShoppingBag, Croissant } from "lucide-react";
import { Reveal } from "./Reveal";

const hotels = [
  {
    id: "ritz",
    name: "The Ritz-Carlton, Sarasota",
    vibe: "The bayside landmark — pool, spa and Sunday brunch worth building a whole weekend around.",
    area: "Marina district",
    map: "https://www.google.com/maps/search/?api=1&query=The+Ritz-Carlton+Sarasota",
  },
  {
    id: "westin",
    name: "The Westin Sarasota",
    vibe: "Modern rooms with marina views, right on the bayfront and minutes from downtown.",
    area: "Bayfront",
    map: "https://www.google.com/maps/search/?api=1&query=The+Westin+Sarasota",
  },
  {
    id: "ovation",
    name: "Art Ovation Hotel",
    vibe: "Boutique, artsy and full of character — walkable to galleries, cafés and downtown nightlife.",
    area: "Arts district",
    map: "https://www.google.com/maps/search/?api=1&query=Art+Ovation+Hotel+Sarasota",
  },
];

export const Hotels = () => (
  <section className="relative overflow-hidden bg-ivory py-24 text-navy-deep sm:py-28" data-testid="hotels-section">
    <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal className="text-center">
        <p className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.4em] text-gold-deep">
          <BedDouble size={14} /> Où dormir ?
        </p>
        <h2 className="mt-4 font-serif text-4xl font-semibold sm:text-5xl lg:text-6xl">Staying the weekend?</h2>
        <p className="mx-auto mt-4 max-w-xl text-sm font-light text-navy/70 sm:text-base">
          The celebration is Saturday evening, and Sarasota is worth the extra night. A few
          places we love:
        </p>
      </Reveal>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {hotels.map((h, i) => (
          <Reveal key={h.id} delay={i * 0.08} className="h-full">
            <article className="flex h-full flex-col rounded-2xl border border-navy/10 bg-white p-7 shadow-[0_10px_30px_rgba(11,19,43,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(153,101,21,0.16)]" data-testid={`hotel-card-${h.id}`}>
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-navy px-3 py-1 text-[9px] font-medium uppercase tracking-[0.18em] text-gold-light">
                <MapPin size={10} /> {h.area}
              </span>
              <h3 className="mt-4 font-serif text-2xl font-semibold leading-snug">{h.name}</h3>
              <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-navy/70">{h.vibe}</p>
              <a
                href={h.map}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`hotel-map-link-${h.id}`}
                className="mt-5 inline-flex w-fit items-center gap-2 border-b border-gold pb-0.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-deep transition-colors duration-300 hover:text-navy"
              >
                View on map
              </a>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <p className="mx-auto mt-10 max-w-xl text-center text-sm font-light text-navy/60">
          December weekends fill up fast in Sarasota — book early. And since the exact address
          is shared after you RSVP, staying central keeps you close either way.
        </p>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-10 rounded-2xl border border-navy/10 bg-white p-8 shadow-[0_10px_30px_rgba(11,19,43,0.06)] sm:p-10" data-testid="while-in-town">
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.4em] text-gold-deep">
            <Palmtree size={14} /> Pendant votre séjour
          </p>
          <h3 className="mt-3 font-serif text-2xl font-semibold sm:text-3xl">While you're in town</h3>
          <div className="mt-7 grid gap-8 sm:grid-cols-3">
            {[
              { icon: Palmtree, title: "Siesta Key Beach", line: "Eight minutes over the bridge — the whitest, softest sand you'll ever sink into." },
              { icon: ShoppingBag, title: "St. Armands Circle", line: "Shops, cafés and gelato around a sunlit circle — a classic Sarasota afternoon." },
              { icon: Croissant, title: "Brunch, sorted", line: "Maple Street Biscuit Company or Another Broken Egg Café — both worth the wait." },
            ].map((g) => (
              <div key={g.title} className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy text-gold-light"><g.icon size={15} /></span>
                <div>
                  <p className="font-serif text-lg font-semibold">{g.title}</p>
                  <p className="mt-1 text-sm font-light leading-relaxed text-navy/70">{g.line}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
